'use client';

import { FC, PropsWithChildren, ReactElement, useContext } from 'react';

import { AssetUtils } from '@ravago/shared/next-data-access';
import { BackgroundColorTokenMap } from '@ravago/shared/page-data/const/background-color-token-map';
import { BackgroundColors } from '@ravago/shared/page-data/enums/background-colors';
import { MaxImageSizes } from '@ravago/shared/page-data/enums/max-image-sizes';
import { NodeTypes } from '@ravago/shared/page-data/enums/node-types';
import { Node } from '@ravago/shared/page-data/models/base';
import { ImageInterface, VideoInterface } from '@ravago/shared/page-data/models/elements';
import { SectionNode } from '@ravago/shared/page-data/models/layout';
import { NodePropertiesGlobals } from '@ravago/shared/page-data/models/utils';
import { Compatibility } from '@ravago/shared/page-data/utils/compatibility';
import { LayoutUtils } from '@ravago/shared/page-data/utils/layout';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Breakpoint, BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';

import { getAlignment } from '../../../utils/alignment/get-alignment';
import { AppUtils } from '../../../utils/app/app.utils';
import { PageRendererContext } from '../../../context/page-renderer-context';
import { isPlaceholderString } from '../../../utils/placeholder/is-placeholder-string';
import { isPlaceholderValid } from '../../../utils/placeholder/is-placeholder-valid';
import { replacePlaceholder } from '../../../utils/placeholder/replace-placeholder';
import { StyleUtils } from '../../../utils/style/style.utils';
import AuthGuard from '../../shared/auth-guard/auth-guard';

import styles from './node-properties.module.scss';

interface Props extends PropsWithChildren {
   node: Node;
   globals?: NodePropertiesGlobals;
   isConsumer: boolean;
   isSelected?: boolean;
   selectedNodeId?: string;
   renderCallback?: (element: ReactElement, priority: string) => ReactElement;
}

const totalDeviceCount = 4;

/* c8 ignore start */
export const NodeProperties: FC<Props> = ({ node, globals: globalsProps, isConsumer, isSelected, selectedNodeId, renderCallback, children }) => {
   const context = useContext(PageRendererContext);
   const locale = globalsProps?.locale ?? context?.locale ?? '';
   const contentEntity = globalsProps?.contentEntity ?? context?.contentEntity ?? null;
   const acceptWebp = globalsProps?.acceptWebp ?? context?.acceptWebp ?? false;
   const assetBasePath = globalsProps?.assetBasePath ?? context?.assetBasePath ?? '';

   const { padding, margin } = node.appearance?.spacing || {};

   const { width, height } = node.appearance?.sizing || {};

   const getBackgroundColor = (): string => {
      if (!node?.appearance?.backgroundColor || BackgroundColorTokenMap[node.appearance.backgroundColor as BackgroundColors] === undefined) return '';

      return BackgroundColorTokenMap[node.appearance.backgroundColor as BackgroundColors];
   };

   const getBackground = (): string => {
      if (!('backgroundImage' in node)) return '';

      const image = node.backgroundImage as ImageInterface;

      if (!image) return '';

      let src: string;

      let rawSrc = image.src;

      const regex = /fields\.(\w+)\.fields\.file\.url/;
      const replacement = '$1.url';

      if (rawSrc) rawSrc = rawSrc.replace(regex, replacement);

      if (isPlaceholderString(rawSrc)) {
         if (isPlaceholderValid(rawSrc, contentEntity)) {
            src = replacePlaceholder(rawSrc, contentEntity, isConsumer, { defaultValue: '' });
         } else {
            src = `${assetBasePath}/images/placeholders/default-banner-large.${acceptWebp ? 'webp' : 'png'}`;
         }
      } else {
         src = rawSrc;
      }

      if (!AssetUtils.optimizeImageIfRequired(src, acceptWebp, image.width, MaxImageSizes.BACKGROUND_IMAGE)) return '';

      return AssetUtils.optimizeImageIfRequired(src, acceptWebp, image.width, MaxImageSizes.BACKGROUND_IMAGE);
   };

   const hasVideoBackground = () => 'backgroundVideo' in node && (node.backgroundVideo as VideoInterface)?.url;

   const hasImageBackground = () => 'backgroundImage' in node && (node.backgroundImage as ImageInterface)?.src;

   const hasImageOverlay = () => 'overlay' in node && node.overlay !== 'none';

   const getImageOverlay = () => {
      if (!('overlay' in node) || !node.overlay || node.overlay === 'none') return undefined;

      if (node.overlay === 'default') return 'scrimmed-default';
      if (node.overlay === 'primary') return 'scrimmed-primary';
      if (node.overlay === 'alternate') return 'scrimmed-alternate';
      if (node.overlay === 'theme') return 'scrimmed-theme';
      if (node.overlay === 'gradient-fade-out-black') return 'gradient-fade-out-black';
      if (node.overlay === 'gradient-fade-out-white') return 'gradient-fade-out-white';
      if (node.overlay === 'gradient-primary') return 'gradient-primary';

      return undefined;
   };

   const hasRoundedCorners = 'rounded' in node && node.rounded;

   const getRoundedCorners = (): 'none' | 'smallest' | 'inset' | 'largest' | 'rounded' | undefined => {
      if (!('rounded' in node)) return undefined;

      if (typeof node.rounded === 'boolean' && node.rounded) return 'largest';
      if (node.rounded === 'xs') return 'smallest';
      if (node.rounded === 'sm') return 'inset';
      if (node.rounded === 'md') return 'largest';
      if (node.rounded === 'lg') return 'largest';
      return undefined;
   };

   const getScreenVisibility = (): Breakpoint[] => {
      const { desktop, tablet, mobile } = node.settings?.visibility || {};

      const breakpoints: Breakpoint[] = [];

      if (desktop) breakpoints.push('desktop');

      if (tablet) breakpoints.push('tab-land', 'tab-port');

      if (mobile) breakpoints.push('phone');

      return breakpoints;
   };

   const getPriority = (): string => {
      if (!isConsumer) {
         if (
            (node.type === NodeTypes.SECTION || LayoutUtils.isRowNode(node as Node) || LayoutUtils.isColumnNode(node as Node)) &&
            'children' in node
         ) {
            const hasSelectedChild = (nodes: any[]): any[] =>
               nodes.find((childNode) => {
                  if (childNode.id === selectedNodeId && childNode.type === NodeTypes.TEXT) {
                     return true;
                  }

                  if ('children' in childNode) {
                     return hasSelectedChild(childNode.children);
                  }

                  return false;
               });

            const hasSelected = hasSelectedChild(node.children as any[]);

            if (hasSelected) return '100';
         }

         if (node.type === NodeTypes.TEXT && isSelected) return '100';
      }

      return 'children' in node && LayoutUtils.priorityElements.some((element) => JSON.stringify(node.children).includes(element)) ? '2' : '1';
   };

   const renderElement = () => (
      <Box
         id={node.settings?.anchor}
         className={StyleUtils.getCssClassesForNode(node as Node)}
         tag={node.type === NodeTypes.SECTION ? 'section' : 'div'}
         position="relative"
         direction="column"
         spacing={Compatibility.getMarginPadding(padding)}
         margin={Compatibility.getMarginPadding(margin)}
         maxWidth={width ? `${width}px` : undefined}
         minHeight={height ? `${height}px` : undefined}
         height={LayoutUtils.isColumnNode(node as Node) ? '100%' : undefined}
         justify={'verticalAlignment' in node ? getAlignment((node as SectionNode).verticalAlignment) : undefined}
         zIndex={getPriority()}
         background={getBackgroundColor()}
         borderRadius={getRoundedCorners()}
         overflowY={node.type === NodeTypes.IMAGE && hasRoundedCorners ? 'hidden' : undefined}
         attributes={{
            ...(node.type && {
               'node-type': node.type,
               'test-selector': AppUtils.getTestSelector('node-properties', node.type, node.settings?.testId)
            })
         }}
      >
         {hasImageOverlay() && !hasImageBackground() && (
            <Box position="absolute" top="0" left="0" zIndex="1" height="100%" width="100%" backgroundColor={getImageOverlay()} />
         )}

         {hasImageBackground() && (
            <Box
               position="absolute"
               top="0"
               left="0"
               zIndex="-1"
               height="100%"
               width="100%"
               borderRadius={getRoundedCorners()}
               overflow="hidden"
               background={`url("${getBackground()}") center / cover no-repeat`}
            >
               {hasImageOverlay() && (
                  <Box position="absolute" top="0" left="0" height="100%" width="100%" backgroundColor={getImageOverlay()} />
               )}
            </Box>
         )}

         {hasVideoBackground() && (
            <div className={styles.videoWrapper}>
               {}
               <video
                  className={styles.video}
                  autoPlay={(node as SectionNode).backgroundVideo?.autoplay}
                  loop={(node as SectionNode).backgroundVideo?.loop}
                  muted={(node as SectionNode).backgroundVideo?.muted}
               >
                  <source src={(node as SectionNode).backgroundVideo?.url} type="video/mp4" />
               </video>
            </div>
         )}

         {children}
      </Box>
   );

   const render = () => (renderCallback ? renderCallback(renderElement(), getPriority()) : renderElement());

   if (!LayoutUtils.isConditionValid(node.settings.conditionals, locale, contentEntity) && isConsumer) return null;

   return (
      <AuthGuard
         redirectUserToLogin={false}
         accessLevels={Compatibility.convertAccessLevel(node.settings?.accessLevel ?? [])}
         isConsumer={isConsumer}
      >
         {getScreenVisibility().length === totalDeviceCount ? (
            render()
         ) : (
            <BreakpointSwitch
               includeViews={getScreenVisibility()}
               useContainerQuery={
                  (isConsumer && node.type !== NodeTypes.SECTION && !LayoutUtils.isRowNode(node as Node)) || !isConsumer
               }
            >
               {render()}
            </BreakpointSwitch>
         )}
      </AuthGuard>
   );
};
/* c8 ignore stop */
